import React, { useEffect, useMemo, useState } from 'react'
import './ModalFrequencySearch.css'

const ModalFrequencySearch = ({closeModal}) => {

    const [symptomSearch, setSymptomSearch] = useState('');
    const [frequencySearch, setFrequencySearch] = useState('');
    const [frequencies, setFrequencies] = useState([]);
    const [resultSummary, setResultSummary] = useState('');
    const [queryLabel, setQueryLabel] = useState('');
    const [searchMode, setSearchMode] = useState('');
    const [summarySearch, setSummarySearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState('');

    useEffect(() => {
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, []);

    const sortedFrequencies = useMemo(() => {
        return [...frequencies].sort((left, right) => Number(right.count ?? 0) - Number(left.count ?? 0));
    }, [frequencies]);

    const summaryItems = useMemo(() => {
        const rawText = String(resultSummary || '').trim();
        if (!rawText) {
            return [];
        }

        return rawText
            .split('||')
            .map((item) => item.replace(/\s+/g, ' ').trim())
            .filter(Boolean);
    }, [resultSummary]);

    const hasDelimitedSummary = summaryItems.length > 1;
    const normalizedSummarySearch = summarySearch.trim().toLowerCase();

    const orderedSummaryItems = useMemo(() => {
        const indexedItems = summaryItems.map((item, index) => ({
            item,
            index,
            isMatch: normalizedSummarySearch ? item.toLowerCase().includes(normalizedSummarySearch) : false,
        }));

        if (!normalizedSummarySearch) {
            return indexedItems;
        }

        return [...indexedItems].sort((left, right) => {
            if (left.isMatch === right.isMatch) {
                return left.index - right.index;
            }

            return left.isMatch ? -1 : 1;
        });
    }, [summaryItems, normalizedSummarySearch]);

    const summaryMatchCount = useMemo(() => {
        if (!normalizedSummarySearch) {
            return 0;
        }

        return summaryItems.filter((item) => item.toLowerCase().includes(normalizedSummarySearch)).length;
    }, [summaryItems, normalizedSummarySearch]);

    const columnSizing = useMemo(() => {
        const measure = (value) => String(value ?? '').trim().length;

        const maxCountChars = sortedFrequencies.reduce((maxChars, row) => {
            return Math.max(maxChars, measure(row?.count));
        }, 'Count'.length);

        const maxFrequencyChars = sortedFrequencies.reduce((maxChars, row) => {
            return Math.max(maxChars, measure(row?.frequency));
        }, 'Frequency'.length);

        const maxSourcesChars = sortedFrequencies.reduce((maxChars, row) => {
            return Math.max(maxChars, measure(row?.sources));
        }, 'Sources'.length);

        const countWidthCh = Math.max(6, Math.min(10, maxCountChars + 2));
        const frequencyWidthCh = Math.max(10, Math.min(16, maxFrequencyChars + 2));
        const sourcesWidthCh = Math.max(14, Math.min(42, maxSourcesChars + 2));

        const includedSymptomsWidthCh = Math.max(26, Math.min(130, 52));

        return {
            countWidthCh,
            frequencyWidthCh,
            sourcesWidthCh,
            includedSymptomsWidthCh,
            tableWidthCh: countWidthCh + frequencyWidthCh + sourcesWidthCh + includedSymptomsWidthCh,
        };
    }, [sortedFrequencies]);

    const executeSearch = async ({ endpoint, nextQueryLabel, mode }) => {
        setSearchError('');
        setIsSearching(true);
        setFrequencies([]);
        setResultSummary('');
        setQueryLabel(nextQueryLabel);
        setSearchMode(mode || '');
        setSummarySearch('');

        try {
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + endpoint,
                {
                    method: 'GET',
                    headers: {
                        'machine':'2',
                        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                        'content-type': 'application/json'
                    }
                });
            if (!response.ok) {
                throw new Error(`Fetch error - ${response.url} - Status: ${response.status}`);
            }

            const json = await response.json();
            const rows = Array.isArray(json) ? json : [];
            setFrequencies(rows);

            if (rows.length > 0) {
                setResultSummary(rows[0].searched_symptoms || `Found ${rows.length} results`);
            } else {
                setResultSummary('No matches found. Try a broader term or different frequency.');
            }
        } catch (error) {
            console.error(error);
            setSearchError('Unable to load search results right now. Please try again.');
        } finally {
            setIsSearching(false);
        }
    }

    const apiGetFrequencies = async (symptom) => {
        await executeSearch({
            endpoint: '/frequencies/symptom/' + encodeURIComponent(symptom),
            nextQueryLabel: `Symptoms searched: ${symptom}`,
            mode: 'symptom',
        });
    }

    const apiGetSymptoms = async (frequency) => {
        await executeSearch({
            endpoint: '/symptoms/frequency/' + encodeURIComponent(frequency),
            nextQueryLabel: `Symptoms treated with ${frequency} Hz`,
            mode: 'frequency',
        });
    }

    const handleChange = (e) => {
        setSymptomSearch(e.target.value);
        setFrequencySearch('');
    }

    const handleFrequencyChange = (e) => {
        setSymptomSearch('');
        setFrequencySearch(e.target.value);
    }

    const handleSearch = async (e) => {
        e.preventDefault();
        const symptom = symptomSearch.trim();
        const frequency = frequencySearch.trim();

        if (symptom) {
            apiGetFrequencies(symptom);
            return;
        }

        if (frequency) {
            apiGetSymptoms(frequency);
            return;
        }

        setSearchError('Enter a symptom or a frequency to search.');
    }

    const handleClear = () => {
        setSymptomSearch('');
        setFrequencySearch('');
        setFrequencies([]);
        setResultSummary('');
        setQueryLabel('');
        setSearchMode('');
        setSummarySearch('');
        setSearchError('');

    }

    const showSymptomsColumn = searchMode !== 'frequency';
    const resultsTableWidthCh = showSymptomsColumn
        ? columnSizing.tableWidthCh
        : (columnSizing.countWidthCh + columnSizing.frequencyWidthCh + columnSizing.sourcesWidthCh);

    const formatFrequencyValue = (value) => {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'number') {
            return Number.isInteger(value) ? String(Math.trunc(value)) : String(value);
        }

        const text = String(value).trim();
        if (!text) {
            return '';
        }

        // Normalize only values that are explicitly integer-like with trailing .0
        if (/^-?\d+\.0+$/.test(text)) {
            return text.replace(/\.0+$/, '');
        }

        return text;
    }

    const renderHighlightedText = (text, searchText) => {
        const normalizedNeedle = String(searchText || '').trim();
        if (!normalizedNeedle) {
            return text;
        }

        const escapedNeedle = normalizedNeedle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const matcher = new RegExp(`(${escapedNeedle})`, 'gi');
        const parts = String(text).split(matcher);

        return parts.map((part, index) => {
            if (part.toLowerCase() === normalizedNeedle.toLowerCase()) {
                return <mark className='modal-frequency-summary-mark' key={`summary-mark-${index}`}>{part}</mark>;
            }

            return <React.Fragment key={`summary-text-${index}`}>{part}</React.Fragment>;
        });
    }

    return (
    <div className='modal-table modal-frequency-overlay' onClick={(e) => {
            if (e.target === e.currentTarget)
            closeModal()}}>
        <div className='modal-sessions modal-frequency'>
            <div className='modal-frequency-header'>
                <h3>Frequency Search</h3>
                <p>Search by symptom or by frequency to quickly inspect treatment overlap.</p>
            </div>

            <form className='modal-frequency-form' onSubmit={handleSearch}>
                <div className='modal-frequency-field modal-frequency-field-symptom'>
                    <label htmlFor='symptom-search' className='modal-frequency-label-symptom'>Symptom</label>
                    <input
                        id='symptom-search'
                        name='symptom'
                        placeholder='Ex: headache, insomnia'
                        value={symptomSearch}
                        onChange={handleChange}
                    />
                </div>

                <div className='modal-frequency-divider'>OR</div>

                <div className='modal-frequency-field modal-frequency-field-frequency'>
                    <label htmlFor='frequency-search' className='modal-frequency-label-frequency'>Frequency</label>
                    <div className='modal-frequency-input-with-unit'>
                        <input
                            id='frequency-search'
                            name='frequency'
                            placeholder='Ex: 432'
                            value={frequencySearch}
                            onChange={handleFrequencyChange}
                        />
                        <span className='modal-frequency-unit'>HZ</span>
                    </div>
                </div>

                <div className='modal-frequency-actions'>
                    <button type='submit' className='btn modal-frequency-search-btn' disabled={isSearching}>
                        {isSearching ? 'Searching...' : 'Search'}
                    </button>
                    <button type='button' className='btn modal-frequency-clear-btn' onClick={handleClear}>
                        Clear
                    </button>
                </div>
            </form>

            {searchError && <div className='modal-frequency-alert error'>{searchError}</div>}

            {(queryLabel || resultSummary) && (
                <div className='modal-frequency-summary'>
                    {queryLabel && <div className='modal-frequency-summary-title'>{queryLabel}</div>}
                    {resultSummary && (
                        hasDelimitedSummary ? (
                            <>
                                <div className='modal-frequency-summary-text'>Matched symptom list:</div>
                                <div className='modal-frequency-summary-filter-row'>
                                    <input
                                        type='text'
                                        className='modal-frequency-summary-filter'
                                        placeholder='Filter symptoms in this list'
                                        value={summarySearch}
                                        onChange={(event) => setSummarySearch(event.target.value)}
                                        aria-label='Filter matched symptoms'
                                    />
                                    {!!normalizedSummarySearch && (
                                        <span className='modal-frequency-summary-filter-count'>{summaryMatchCount} match(es)</span>
                                    )}
                                </div>
                                <div className='modal-frequency-summary-tags'>
                                    {orderedSummaryItems.map(({ item, index, isMatch }) => (
                                        <span
                                            className={`modal-frequency-summary-tag${normalizedSummarySearch && isMatch ? ' is-match' : ''}`}
                                            key={`summary-item-${index}`}
                                        >
                                            {renderHighlightedText(item, normalizedSummarySearch)}
                                        </span>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className='modal-frequency-summary-text'>{resultSummary}</div>
                        )
                    )}
                    {!isSearching && <div className='modal-frequency-summary-count'>{sortedFrequencies.length} result(s)</div>}
                </div>
            )}

            <div className='modal-frequency-results-scroll'>
                <table className='table-sessions modal-frequency-results' style={{ width: `${resultsTableWidthCh}ch` }}>
                    <colgroup>
                        <col style={{ width: `${columnSizing.countWidthCh}ch` }} />
                        <col style={{ width: `${columnSizing.frequencyWidthCh}ch` }} />
                        <col style={{ width: `${columnSizing.sourcesWidthCh}ch` }} />
                        {showSymptomsColumn && <col style={{ width: `${columnSizing.includedSymptomsWidthCh}ch` }} />}
                    </colgroup>
                    <thead>
                        <tr>
                            <th className='center'>Count</th>
                            <th className='center'>Frequency</th>
                            <th className='center'>Sources</th>
                            {showSymptomsColumn && <th className='left'>Included Symptoms</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {isSearching ? (
                            Array.from({ length: 5 }).map((_, rowIndex) => (
                                <tr key={`frequency-search-skeleton-${rowIndex}`} className='table-skeleton-row'>
                                    <td><span className='table-skeleton-line short' /></td>
                                    <td><span className='table-skeleton-line short' /></td>
                                    <td><span className='table-skeleton-line' /></td>
                                    {showSymptomsColumn && <td><span className='table-skeleton-line' /></td>}
                                </tr>
                            ))
                        ) : sortedFrequencies.length > 0 ? (
                            sortedFrequencies.map((freq, i) => (
                                <tr key={`${freq.frequency ?? 'freq'}-${i}`}>
                                    <td className='center'>{freq.count}</td>
                                    <td className='center'>{formatFrequencyValue(freq.frequency)}</td>
                                    <td className='left-nowrap'>{freq.sources}</td>
                                    {showSymptomsColumn && <td className='left-wrap'>{freq.symptoms}</td>}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className='center modal-frequency-empty' colSpan={showSymptomsColumn ? 4 : 3}>Run a search to view frequencies and symptoms.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <button type='button' className='btn modal-frequency-close-btn' onClick={closeModal}>Close</button>
            </div>
        </div>

  )

}

export default ModalFrequencySearch
